import React, { useEffect, useRef, useState } from 'react';

import { Icon, Spinner } from '@components';

import { BulkAssignee } from '../types/common';

interface BulkAssignBottomBarProps {
  selectedCount: number;
  assigneeOptions: BulkAssignee[];
  selectedAssigneeId: string;
  onSelectAssignee: (assigneeId: string) => void;
  onClearSelection: () => void;
  onAssign: () => void;
  isAssigning?: boolean;
}

const BulkAssignBottomBar: React.FC<BulkAssignBottomBarProps> = ({
  selectedCount,
  assigneeOptions,
  selectedAssigneeId,
  onSelectAssignee,
  onClearSelection,
  onAssign,
  isAssigning = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedAssignee = assigneeOptions.find(
    (option) => option.id === selectedAssigneeId
  );

  const handleOptionSelect = (assigneeId: string) => {
    onSelectAssignee(assigneeId);
    setIsDropdownOpen(false);
  };

  const isAssignDisabled = !selectedAssigneeId || isAssigning;

  return (
    <div className="pointer-events-none fixed bottom-20 left-55 right-0 z-50 flex items-center justify-center px-2">
      <div className="pointer-events-auto flex items-center gap-4 rounded-2xl bg-textblack px-5 py-3 shadow-[0px_6px_16px_0px_rgba(0,0,0,0.3)]">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClearSelection}
            className="flex size-4 items-center justify-center"
            aria-label="Clear selection"
          >
            <Icon name="close-white" size="16" />
          </button>
          <span className="whitespace-nowrap text-base font-medium text-white">
            <span className="inline-block min-w-7.5 text-right tabular-nums">
              {selectedCount}
            </span>{' '}
            selected
          </span>
        </div>

        <div className="h-6 w-px bg-cofeeNeutral14" />

        <div className="flex items-center gap-3">
          <span className="whitespace-nowrap text-base font-normal text-cofeeNeutral6">
            Assign leads to:
          </span>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen((isOpen) => !isOpen)}
              className="flex h-9 w-52 items-center justify-between gap-2 rounded-xl bg-darkGrey px-4"
            >
              <span
                className={`truncate text-base font-normal ${
                  selectedAssignee ? 'text-white' : 'text-cofeeNeutral7'
                }`}
              >
                {selectedAssignee ? selectedAssignee.name : 'Select assignee'}
              </span>
              <Icon
                name="down-icon-white"
                size="16"
                className={`shrink-0 transition-transform ${
                  isDropdownOpen ? '-rotate-180' : ''
                }`}
              />
            </button>

            {isDropdownOpen && (
              <div className="dark-scroll absolute bottom-full right-0 mb-3 max-h-64 w-60 overflow-y-auto rounded-2xl bg-textblack py-2 shadow-[0px_8px_20px_0px_rgba(0,0,0,0.4)]">
                {assigneeOptions.length === 0 ? (
                  <div className="px-5 py-3 text-base font-normal text-cofeeNeutral7">
                    No assignees available
                  </div>
                ) : (
                  assigneeOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleOptionSelect(option.id)}
                      className="flex w-full items-center justify-between gap-2 px-5 py-3 text-left hover:bg-darkGrey"
                    >
                      <span className="truncate text-base font-normal text-white">
                        {option.name}
                      </span>
                      {option.id === selectedAssigneeId && (
                        <Icon name="check-icon-green" size="16" />
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onAssign}
            disabled={isAssignDisabled}
            className={`flex h-8 w-[75px] items-center justify-center rounded-[4px] text-base font-medium transition-colors ${
              isAssignDisabled
                ? 'cursor-not-allowed bg-cofeeNeutral14 text-cofeeNeutral6'
                : 'bg-cofeePrimary6 text-white hover:bg-cofeePrimary7'
            }`}
          >
            {isAssigning ? <Spinner size="sm" /> : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignBottomBar;
